import { Alert, Page } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import type { AlertVariant } from '../../../components/ui/alert/types';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { useUpdateSettings } from '../../../context/settings-context';
import { PARTNERS_I18N } from '../constants';
import { OAUTH, type OAuthPartnerId } from '../oauth';

const CALLBACK = PARTNERS_I18N.oauthCallback;

export type OAuthCallbackPageProps = {
  partner: OAuthPartnerId;
};

export const PartnerOAuthCallbackPage = withPageProviders(OAuthCallbackContent);

function OAuthCallbackContent({ partner }: OAuthCallbackPageProps) {
  const { navigationHandler } = useHandlers();
  const query = navigationHandler.query();
  const config = OAUTH[partner];
  const { mutate, isPending, isError } = useUpdateSettings();

  // Read the stored state token on first render — before the effect below clears it. Reading it
  // inside the effect would always see `null` and every callback would report a state mismatch.
  const [storedState] = useState<string | null>(() => localStorage.getItem(config.stateKey));
  const [storedSettingsJson] = useState<string | null>(() =>
    config.settingsKey ? localStorage.getItem(config.settingsKey) : null,
  );
  const [unusableQuery, setUnusableQuery] = useState<boolean>(false);
  // False until the effect has validated the state token and issued (or skipped) the PATCH, so the
  // first paint shows "connecting" rather than flashing "connected" like the Vue onMounted never did.
  const [checked, setChecked] = useState<boolean>(false);

  // The page must never issue its settings PATCH twice (double-invoke safety).
  const attempted = useRef(false);
  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    localStorage.removeItem(config.stateKey);
    if (config.settingsKey) {
      localStorage.removeItem(config.settingsKey);
    }

    if (storedState !== query.state) {
      setChecked(true);
      return;
    }

    const payload = config.buildSettings(query, storedSettingsJson);
    if (payload) {
      mutate(payload);
    } else {
      setUnusableQuery(true);
    }
    setChecked(true);
  }, [config, query, storedSettingsJson, storedState, mutate]);

  const matched = storedState === query.state;
  const { variant, message } = resolveAlert({
    matched,
    settled: checked && !isPending,
    failed: isError || unusableQuery,
    partner: config.name,
  });

  return (
    <Page title={config.name} description={t(`${CALLBACK}.description`)}>
      <Alert variant={variant} headingLevel="h2">
        {message}
      </Alert>
    </Page>
  );
}

function resolveAlert({
  matched,
  settled,
  failed,
  partner,
}: {
  matched: boolean;
  settled: boolean;
  failed: boolean;
  partner: string;
}): { variant: AlertVariant; message: string } {
  if (!matched) {
    return { variant: 'error', message: t(`${CALLBACK}.state_mismatch`) };
  }
  if (!settled) {
    return { variant: 'warning', message: t(`${CALLBACK}.connecting`, { partner }) };
  }
  if (failed) {
    return { variant: 'error', message: t(`${CALLBACK}.error`) };
  }
  return { variant: 'success', message: t(`${CALLBACK}.success`) };
}
